import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, ENDPOINTS } from '../services';

export const createAIProjectSetting = createAsyncThunk('aiDev/createProjectSetting', async (payload) => {
  const response = await api.post(ENDPOINTS.AI_PROJECT_SETTING, payload);
  return response.data;
});

export const fetchAIProjectSettings = createAsyncThunk('aiDev/fetchProjectSettings', async () => {
  const response = await api.get(ENDPOINTS.AI_PROJECT_SETTING_ALL);
  return response.data;
});

export const deleteAIProjectSetting = createAsyncThunk('aiDev/deleteProjectSetting', async (id) => {
  await api.delete(ENDPOINTS.AI_PROJECT_SETTING_BY_ID(id));
  return id;
});

export const generateAIPlan = createAsyncThunk('aiDev/generatePlan', async (payload) => {
  const response = await api.post(ENDPOINTS.AI_PLAN, payload);
  return response.data;
});

export const fetchAIPlanByTaskId = createAsyncThunk('aiDev/fetchPlanByTaskId', async (external_task_id) => {
  const response = await api.get(ENDPOINTS.AI_PLAN_BY_TASK_ID(external_task_id));
  return response.data;
});

export const fetchAIPlanDropdown = createAsyncThunk('aiDev/fetchPlanDropdown', async ({ projectId, type }) => {
  const response = await api.get(`${ENDPOINTS.AI_PLAN_DROPDOWN(projectId)}&type=${type}`);
  return response.data;
});

export const updateAIPlan = createAsyncThunk('aiDev/updatePlan', async ({ id, task }) => {
  const response = await api.put(ENDPOINTS.AI_PLAN_BY_ID(id), {
    task
  });
  return response.data;
});

export const updateAIPlanApproval = createAsyncThunk('aiDev/updatePlanApproval', async ({ approvalId, status, planId }) => {
  const response = await api.put(ENDPOINTS.AI_PLAN_APPROVAL(approvalId), {
    status,
    planId
  });
  return response.data;
});

export const fetchAICodeGeneration = createAsyncThunk('aiDev/fetchCodeGeneration', async (external_task_id) => {
  const response = await api.get(ENDPOINTS.AI_CODE_GENERATION(external_task_id));
  return response.data;
});

const aiDevSlice = createSlice({
  name: 'aiDev',
  initialState: {
    settings: [],
    loading: false,
    error: null,
    planLoading: false,
    planError: null,
    planData: null,
    dropdownData: null,
    conditionTypes: null,
    userEmails: null,
    dropdownLoading: false,
    dropdownError: null,
    codeGenerationData: null,
    codeGenerationLoading: false,
    codeGenerationError: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createAIProjectSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAIProjectSetting.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAIProjectSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAIProjectSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAIProjectSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload.data || [];
      })
      .addCase(fetchAIProjectSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteAIProjectSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAIProjectSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = state.settings.filter(setting => setting.id !== action.payload);
      })
      .addCase(deleteAIProjectSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(generateAIPlan.pending, (state) => {
        state.planLoading = true;
        state.planError = null;
      })
      .addCase(generateAIPlan.fulfilled, (state) => {
        state.planLoading = false;
      })
      .addCase(generateAIPlan.rejected, (state, action) => {
        state.planLoading = false;
        state.planError = action.error.message;
      })
      .addCase(fetchAIPlanByTaskId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAIPlanByTaskId.fulfilled, (state, action) => {
        state.loading = false;
        state.planData = action.payload;
      })
      .addCase(fetchAIPlanByTaskId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAIPlanDropdown.pending, (state) => {
        state.dropdownLoading = true;
        state.dropdownError = null;
      })
      .addCase(fetchAIPlanDropdown.fulfilled, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownData = action.payload;
        // Store data separately based on type
        if (action.payload?.data?.condition_type) {
          state.conditionTypes = action.payload.data.condition_type;
        }
        if (action.payload?.data?.user_emails) {
          state.userEmails = action.payload.data.user_emails;
        }
      })
      .addCase(fetchAIPlanDropdown.rejected, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownError = action.error.message;
      })
      .addCase(updateAIPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAIPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.planData = action.payload;
      })
      .addCase(updateAIPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateAIPlanApproval.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAIPlanApproval.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateAIPlanApproval.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAICodeGeneration.pending, (state) => {
        state.codeGenerationLoading = true;
        state.codeGenerationError = null;
      })
      .addCase(fetchAICodeGeneration.fulfilled, (state, action) => {
        state.codeGenerationLoading = false;
        state.codeGenerationData = action.payload;
      })
      .addCase(fetchAICodeGeneration.rejected, (state, action) => {
        state.codeGenerationLoading = false;
        state.codeGenerationError = action.error.message;
      });
  }
});

export default aiDevSlice.reducer;
